import Modal from '@sdk/components/ui/modal/Modal';
import Row from '@sdk/components/ui/spacing/Row';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { pgettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import { ChooseAttestationToReplaceModal } from '@web/timesheets/EditTimesheetModal/components/modals/ChooseAttestationToReplaceModal';
import TimesheetViewModalHeader from '@web/timesheets/TimesheetViewModal/components/TimesheetViewModalHeader/TimesheetViewModalHeader';
import { EditTimesheetModalProps } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.types';
import {
  ModalWrapper,
  Content,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.styles';
import { useEditTimesheetModal } from '@web/timesheets/EditTimesheetModal/editTimesheetModal.hooks';
import CreateTimesheetModalFooter from '@web/timesheets/CreateTimesheetModal/components/CreateTimesheetModalFooter/CreateTimesheetModalFooter';
import ScheduledRow from '@web/timesheets/EditTimesheetModal/components/rows/ScheduledRow/ScheduledRow';
import DateRow from '@web/timesheets/CreateTimesheetModal/components/rows/DateRow/DateRow';
import TimeClockSections from '@web/timesheets/CreateTimesheetModal/components/TimeClockSections/TimeClockSections';
import DurationRow from '@web/timesheets/CreateTimesheetModal/components/rows/DurationRow/DurationRow';
import AttestationsRow from '@web/timeClock/modals/rows/AttestationsRow/AttestationsRow';
import AddEntryButton from '@web/timesheets/CreateTimesheetModal/components/buttons/AddEntryButton';
import TimesheetHistory from '@web/timeClock/TimesheetHistory/TimesheetHistory';
import { ConfirmExcessiveBreaksModal } from '@web/timesheets/EditTimesheetModal/components/modals/ConfirmExcessiveBreaksModal';

const EditTimesheetModal = ({ isVisible, shift, onSave, onClose }: EditTimesheetModalProps) => {
  const {
    modalConfig,
    fields,
    dynamicLists,
    addEntry,
    canSubmit,
    handleSubmit,
    submitErrors,
    setSubmitErrors,
    isAttestationVisible,
    timesheetProjectionsWithAttstations,
    breakAttestations,
    isHistoryView,
    toggleHistoryView,
    historyItems,
    scheduledShiftStart,
    isUnscheduled,
    confirmExcessiveBreaksModalState,
    chooseAttestationToReplaceModalState,
  } = useEditTimesheetModal({
    onSave,
    shift,
  });

  if (isHistoryView) {
    return (
      <Modal isVisible={isVisible} fixed config={modalConfig}>
        <ModalWrapper>
          <TimesheetViewModalHeader
            onClose={onClose}
            shift={shift}
            toggleHistory={toggleHistoryView}
            isHistoryView={isHistoryView}
            showHistoryButton={Boolean(historyItems?.length)}
          />
          <TimesheetHistory historyItems={historyItems} back={toggleHistoryView} />
        </ModalWrapper>
      </Modal>
    );
  }

  return (
    <>
      <Modal isVisible={isVisible} fixed config={modalConfig}>
        <ModalWrapper>
          <TimesheetViewModalHeader
            onClose={onClose}
            shift={shift}
            isHistoryView={false}
            toggleHistory={toggleHistoryView}
            showHistoryButton={Boolean(historyItems?.length)}
          />
          <Content>
            <ScheduledRow shift={shift} />
            <Row mTop={10} mBottom={10}>
              <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
            </Row>
            <DateRow dateField={fields.date} isReadOnly={!isUnscheduled} />
            <TimeClockSections
              timeClockFields={dynamicLists.timeClock.fields}
              submitErrors={submitErrors}
              setSubmitErrors={setSubmitErrors}
              scheduledShiftStart={scheduledShiftStart}
              breakAttestations={breakAttestations}
              askForExcessiveBreaksConfirmation={() => confirmExcessiveBreaksModalState.getResult()}
              chooseAttestationToReplace={(arg) =>
                chooseAttestationToReplaceModalState.getResult(arg)
              }
            />
            <DurationRow
              shift={shift}
              timeClockEntries={dynamicLists.timeClock.value}
              entriesDate={fields.date.value}
              breakAttestations={breakAttestations}
            />
            {isAttestationVisible && (
              <>
                <Row mTop={10}>
                  <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
                </Row>
                <AttestationsRow timesheetProjections={timesheetProjectionsWithAttstations} />
                <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
              </>
            )}
            <Row mTop={10} justifyContent="flex-end">
              <AddEntryButton
                label={pgettext(
                  'Adding new time clock (clock in - clock out) section when editing timesheet',
                  'Add entry'
                )}
                onClick={addEntry}
              />
            </Row>
          </Content>
          <CreateTimesheetModalFooter
            onSave={handleSubmit}
            onClose={onClose}
            canSubmit={canSubmit}
          />
        </ModalWrapper>
      </Modal>
      <ConfirmExcessiveBreaksModal {...confirmExcessiveBreaksModalState} />
      <ChooseAttestationToReplaceModal {...chooseAttestationToReplaceModalState} />
    </>
  );
};

export default EditTimesheetModal;
